import { useEffect, useState, useMemo, useRef, Fragment } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import API, { getErrorMessage } from "../api/axios";
import { fetchPagedCollection } from "../api/paginated";
import { hasFullAccess, getUser } from "../auth/auth";
import { useToast } from "../components/Toast";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Collapse,
  Grid,
  Stack,
  Tooltip,
  Autocomplete,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import PlaceIcon from "@mui/icons-material/Place";
import WorkOutlineIcon from "@mui/icons-material/Work";
import { ACCENT } from "../theme";
import EmptyState from "../components/ui/EmptyState";
import StatusBadge from "../components/ui/StatusBadge";
import SearchInput from "../components/ui/SearchInput";
import FilterBar from "../components/ui/FilterBar";
import SavedViewTabs from "../components/ui/SavedViewTabs";
import FilterChipBar from "../components/ui/FilterChipBar";
import BulkActionBar from "../components/ui/BulkActionBar";
import SideDrawer from "../components/ui/SideDrawer";
import PageContainer from "../components/ui/PageContainer";
import DateFilter from "../components/ui/DateFilter";
import RefreshButton from "../components/ui/RefreshButton";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Checkbox from "@mui/material/Checkbox";

// Maps this file's existing MUI Chip `color` names (used for position
// status, freshness, and candidate feedback) to the STATUS_COLORS tone keys
// from Phase 1/theme.js. "secondary" (used only for DUPLICATE feedback here)
// has no exact equivalent among the shared tones — theme.js's `secondary`
// palette is NAVY, a dark navy, not one of the 7 STATUS_COLORS hues — so it's
// mapped to "neutral" as the closest approximation. This changes only the
// rendered tint for that one case, not the label or the underlying feedback
// value.
const MUI_COLOR_TO_TONE = {
  success: "ok",
  info: "info",
  primary: "info",
  warning: "warn",
  error: "danger",
  default: "neutral",
  secondary: "neutral",
};

// ─── Constants ───────────────────────────────────────────────────────────────

// Default companies that are ALWAYS available in the dropdown, even before any
// position uses them. Any other company already present in the data is added
// dynamically (see companyOptions useMemo below), so a new company like
// "Infosys" appears automatically once one position uses it.
const DEFAULT_COMPANIES = [
  "Walking Tree",
  "Profinch",
  "TCS",
  "Cerabraix",
  "Internal",
];

// Normalize so "PROFINCH" / "Profinch" / "Profinch " merge to one entry.
const normCompany = (s) => (s || "").trim().toLowerCase();

const emptyForm = {
  jobCode: "",
  positionDate: "",
  numberOfPositions: "",
  companyName: "",
  domain: "",
  roleName: "",
  skills: "",
  relevantSkills: "",
  mandatorySkills: "",
  jobDescription: "",
  salary: "",
  experience: "",
  relevantExperience: "",
  location: "",
  spocName: "",
  assignedRecruiter: "",
  status: "ACTIVE",
};

const fields = [
  { label: "Job Code", key: "jobCode", required: true },
  { label: "Position Date", key: "positionDate", type: "date" },
  { label: "Company Name", key: "companyName", required: true },
  { label: "Domain", key: "domain" },
  { label: "Role Name", key: "roleName", required: true },
  { label: "Skills", key: "skills" },
  { label: "Relevant Skills", key: "relevantSkills" },
  { label: "Mandatory Skills", key: "mandatorySkills" },
  { label: "Salary", key: "salary" },
  { label: "Experience", key: "experience" },
  { label: "Relevant Experience", key: "relevantExperience" },
  { label: "Location", key: "location" },
  { label: "SPOC Name", key: "spocName" },
  { label: "Number of Positions", key: "numberOfPositions", type: "number" },
];

const STATUS_COLOR = {
  SHORTLISTED: "success",
  SELECTED: "info",
  L1_ROUND: "info",
  L2_ROUND: "primary",
  ON_HOLD: "warning",
  REJECTED: "error",
  PENDING: "default",
  DUPLICATE: "secondary",
};

const statusLabel = (k) =>
  ({
    SHORTLISTED: "Shortlisted",
    SELECTED: "Selected",
    L1_ROUND: "L1 Round",
    L2_ROUND: "L2 Round",
    ON_HOLD: "On Hold",
    REJECTED: "Rejected",
    PENDING: "Pending",
    DUPLICATE: "Duplicate",
  })[k] ??
  k ??
  "Pending";

// Colors for the mini pipeline bar (Positions table row) — a compact
// visual breakdown of this position's candidates by real stage. Distinct
// from StatusBadge's tone system since this needs to render as tiny
// solid-color segments, not text chips.
const MINI_BAR_COLOR = {
  PENDING: "#94a3b8",
  SHORTLISTED: "#2563eb",
  L1_ROUND: "#7c3aed",
  L2_ROUND: "#0d9488",
  ON_HOLD: "#d97706",
  SELECTED: "#059669",
  REJECTED: "#dc2626",
  DUPLICATE: "#6b7280",
};

const POSITION_STATUS_OPTIONS = ["ACTIVE", "ON_HOLD", "CLOSED"];
const POSITION_STATUS_LABEL = {
  ACTIVE: "Open",
  ON_HOLD: "On Hold",
  CLOSED: "Closed",
};
const POSITION_STATUS_COLOR = {
  ACTIVE: "success",
  ON_HOLD: "warning",
  CLOSED: "default",
};

const posStatusLabel = (k) => POSITION_STATUS_LABEL[k] ?? "Open";
const posStatusColor = (k) => POSITION_STATUS_COLOR[k] ?? "success";

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Timezone-safe date display (dd/mm/yyyy), no new Date() shift
const fmtDate = (value) => {
  if (!value) return "—";
  const s = String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return s;
};

// YYYY-MM-DD prefix for date-range compare (no timezone shift)
const dateOnly = (value) => {
  if (!value) return null;
  const s = String(value);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
};

const todayStr = (() => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
})();

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function freshness(job) {
  const raw = job.positionDate || job.createdAt;
  if (!raw) return { label: "—", color: "default", days: null };
  const days = Math.round(
    (startOfDay(new Date()) - startOfDay(raw)) / 86_400_000,
  );
  if (days <= 1) return { label: "Fresh", color: "success", days };
  if (days <= 3) return { label: "Aging", color: "warning", days };
  return { label: "Old", color: "error", days };
}

function candidateName(c) {
  const fn = [c.firstName, c.lastName].filter(Boolean).join(" ").trim();
  return fn || c.candidateName || "Unnamed";
}

// Kept as a thin wrapper (rather than inlining getErrorMessage everywhere)
// so the console.error() call sites below still have a single place to grab
// "the message we showed the user" for logging.
function extractErrorMessage(err) {
  return getErrorMessage(err);
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function JobRequirements() {
  const toast = useToast();
  const fullAccess = hasFullAccess();

  const [jobs, setJobs] = useState([]);
  const [cands, setCands] = useState([]);
  const [tablePage, setTablePage] = useState(1);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const [filterCompany, setFilterCompany] = useState("");
  const [filterRecruiter, setFilterRecruiter] = useState("");
  // Age filter chips (Fresh/Aging/Old, matching the existing freshness()
  // categories already used for each row's freshness badge — same 3
  // buckets, just exposed as a quick filter too). Skills is intentionally
  // NOT added as a separate chip filter: the existing search box already
  // matches against j.skills, so a dedicated Skills filter would just
  // duplicate that.
  const [ageFilter, setAgeFilter] = useState("");
  // Density toggle — purely visual row-height/padding control. Defaults to
  // "compact" because the table was ALREADY hardcoded to size="small"
  // before this change — this default preserves the exact existing look
  // for anyone who never touches the toggle.
  const [density, setDensity] = useState("compact");
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [openRow, setOpenRow] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  // Recruiter/Manager users, for the "Assigned Recruiter" dropdown
  const [recruiters, setRecruiters] = useState([]);
  // Registered client companies (strict Company dropdown source)
  const [clientCompanies, setClientCompanies] = useState([]);
  // SPOCs of the currently-selected company (free-solo SPOC dropdown)
  const [companySpocs, setCompanySpocs] = useState([]);

  // ── Phase 7 (Design Gap Audit item G) additions ─────────────────────────────
  // Saved view: a purely frontend filter on top of the existing search/
  // company/recruiter/date filters below. No new API/backend involved.
  const [savedView, setSavedView] = useState("all");
  // Bulk selection: a Set of selected jobCodes.
  const [selectedCodes, setSelectedCodes] = useState(new Set());
  // Drawer: added as a NEW "View" action alongside the existing row-click
  // inline expand (openRow/Collapse below), not a replacement of it. The
  // existing expand-on-row-click is a real, currently-used interaction
  // (Edit/Delete already coexist with it via stopPropagation on their
  // column) — swapping it for a drawer outright is a bigger behavioral
  // change than this pass makes unilaterally. The SideDrawer is wired in
  // and fully functional via the new "View" button per row; row-click can
  // be switched to open it instead once you've confirmed the drawer
  // content/tabs look right.
  const [drawerJob, setDrawerJob] = useState(null);
  const [drawerTab, setDrawerTab] = useState(0);

  // ── Data fetching ───────────────────────────────────────────────────────────

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = () => {
    fetchPagedCollection("/jobs", setJobs).catch((err) => {
      console.error("[Positions] positions load failed:", err);
      toast.error(getErrorMessage(err, "Could not load positions"));
    });
    fetchPagedCollection("/candidates", setCands).catch((err) => {
      console.error("[Positions] candidate summary load failed:", err);
    });
    // Recruiters + managers, for the Assigned Recruiter dropdown.
    // Endpoint is ADMIN/MANAGER-only — the same people who can add positions —
    // so it's fine here. If it fails (e.g. permissions), we just fall back to a
    // free-typed name.
    API.get("/account/users")
      .then((r) => {
        const names = (r.data || [])
          .filter((u) => u.role === "RECRUITER" || u.role === "MANAGER")
          .map((u) => u.name)
          .filter(Boolean);
        setRecruiters([...new Set(names)].sort((a, b) => a.localeCompare(b)));
      })
      .catch(() => setRecruiters([]));
    // Registered client companies for the strict Company dropdown.
    API.get("/clients")
      .then((r) => setClientCompanies(r.data || []))
      .catch(() => setClientCompanies([]));
  };

  // Load the SPOCs belonging to the selected company (for the SPOC dropdown).
  const loadSpocsForCompany = (companyName) => {
    const target = (companyName || "").trim().toLowerCase();
    const company = clientCompanies.find(
      (c) => (c.name || "").trim().toLowerCase() === target,
    );
    if (!company) {
      setCompanySpocs([]);
      return;
    }
    API.get(`/clients/${company.id}/spocs`)
      .then((r) => setCompanySpocs(r.data || []))
      .catch(() => setCompanySpocs([]));
  };

  // Dynamic company list for the Add/Edit dropdown: defaults + any company that
  // already exists in the saved positions, de-duplicated case/space-insensitively.
  const companyOptions = useMemo(() => {
    const map = new Map(); // normalized -> display label
    DEFAULT_COMPANIES.forEach((c) => map.set(normCompany(c), c));
    jobs.forEach((j) => {
      const raw = (j.companyName || "").trim();
      if (!raw) return;
      const key = normCompany(raw);
      if (!map.has(key)) map.set(key, raw);
    });
    return [...map.values()].sort((a, b) => a.localeCompare(b));
  }, [jobs]);

  // Recruiter dropdown options: the real RECRUITER/MANAGER users, PLUS any
  // recruiter name already saved on a position (so a previously-typed name
  // still shows up), de-duplicated.
  const recruiterOptions = useMemo(() => {
    const set = new Set();
    recruiters.forEach((n) => n && set.add(n.trim()));
    jobs.forEach((j) => {
      // a position may now have several comma-separated recruiters
      (j.assignedRecruiter || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .forEach((n) => set.add(n));
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [recruiters, jobs]);

  // ── Form helpers ────────────────────────────────────────────────────────────

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const resetForm = () => {
    setForm(emptyForm);
    setEditId(null);
    setShowForm(false);
  };

  // ── Derived data ────────────────────────────────────────────────────────────

  const candsByJob = useMemo(() => {
    const m = {};
    cands.forEach((c) => {
      if (!c.jobCode) return;
      (m[c.jobCode] = m[c.jobCode] || []).push(c);
    });
    return m;
  }, [cands]);

  const recruiterCount = (jobCode) =>
    new Set(
      (candsByJob[jobCode] || []).map((c) => c.recruiterName).filter(Boolean),
    ).size;

  const recruiterNames = (jobCode) => [
    ...new Set(
      (candsByJob[jobCode] || []).map((c) => c.recruiterName).filter(Boolean),
    ),
  ];

  const inDateRange = (j) => {
    if (!fromDate && !toDate) return true;
    const ds = dateOnly(j.positionDate || j.createdAt);
    if (!ds) return false;
    if (fromDate && ds < fromDate) return false;
    if (toDate && ds > toDate) return false;
    return true;
  };

  const visible = jobs
    .filter((j) =>
      [
        j.companyName,
        j.jobCode,
        j.roleName,
        j.location,
        j.skills,
        j.domain,
      ].some((v) => v?.toLowerCase().includes(search.toLowerCase())),
    )
    .filter((j) => !filterCompany || j.companyName === filterCompany)
    .filter(
      (j) =>
        !filterRecruiter ||
        (j.assignedRecruiter || "")
          .split(",")
          .map((s) => s.trim())
          .includes(filterRecruiter),
    )
    .filter(inDateRange)
    .filter((j) => !ageFilter || freshness(j).label === ageFilter);

  // ── Phase 7: Saved views (item G.1) ─────────────────────────────────────────
  // A saved view is just an additional predicate layered on top of the
  // existing `visible` filtering above — it does not replace or bypass any
  // existing filter, and every count below comes from real, already-loaded
  // `visible` data (never hardcoded).
  const myName = getUser()?.name;
  const matchesSavedView = (j) => {
    switch (savedView) {
      case "open":
        return (
          (j.status || "ACTIVE") !== "ON_HOLD" &&
          (j.status || "ACTIVE") !== "CLOSED"
        );
      case "onHold":
        return j.status === "ON_HOLD";
      case "closed":
        return j.status === "CLOSED";
      case "noCandidates":
        return (candsByJob[j.jobCode] || []).length === 0;
      case "aging7":
        return (freshness(j).days || 0) > 7;
      case "mine":
        return myName ? (j.assignedRecruiter || "").includes(myName) : false;
      case "interviewing":
        return (candsByJob[j.jobCode] || []).some(
          (c) => c.feedback === "L1_ROUND" || c.feedback === "L2_ROUND",
        );
      default:
        return true;
    }
  };
  const savedViewDefs = [
    { key: "all", label: "All", count: visible.length },
    {
      key: "open",
      label: "Open",
      count: visible.filter(
        (j) =>
          (j.status || "ACTIVE") !== "ON_HOLD" &&
          (j.status || "ACTIVE") !== "CLOSED",
      ).length,
    },
    {
      key: "onHold",
      label: "On hold",
      count: visible.filter((j) => j.status === "ON_HOLD").length,
    },
    {
      key: "closed",
      label: "Closed",
      count: visible.filter((j) => j.status === "CLOSED").length,
    },
    {
      key: "noCandidates",
      label: "Needs sourcing",
      count: visible.filter((j) => (candsByJob[j.jobCode] || []).length === 0)
        .length,
    },
    {
      key: "aging7",
      label: "Ageing >7d",
      count: visible.filter((j) => (freshness(j).days || 0) > 7).length,
    },
    ...(myName
      ? [
          {
            key: "mine",
            label: "My roles",
            count: visible.filter((j) =>
              (j.assignedRecruiter || "").includes(myName),
            ).length,
          },
        ]
      : []),
    {
      key: "interviewing",
      label: "Interviewing",
      count: visible.filter((j) =>
        (candsByJob[j.jobCode] || []).some(
          (c) => c.feedback === "L1_ROUND" || c.feedback === "L2_ROUND",
        ),
      ).length,
    },
  ];
  const visibleAfterView = visible.filter(matchesSavedView);
  const rowsPerPage = 50;
  const pageCount = Math.max(1, Math.ceil(visibleAfterView.length / rowsPerPage));
  const visiblePage = visibleAfterView.slice(
    (tablePage - 1) * rowsPerPage,
    tablePage * rowsPerPage,
  );
  useEffect(() => {
    setTablePage(1);
  }, [search, filterCompany, filterRecruiter, ageFilter, fromDate, toDate, savedView]);

  // ── Phase 7: Bulk selection (item G.4-5) ────────────────────────────────────
  const toggleSelect = (jobCode) => {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      if (next.has(jobCode)) next.delete(jobCode);
      else next.add(jobCode);
      return next;
    });
  };
  const toggleSelectAllVisible = () => {
    setSelectedCodes((prev) => {
      const allSelected =
        visibleAfterView.length > 0 &&
        visibleAfterView.every((j) => prev.has(j.jobCode));
      if (allSelected) return new Set();
      return new Set(visibleAfterView.map((j) => j.jobCode));
    });
  };
  const clearSelection = () => setSelectedCodes(new Set());

  // Bulk delete reuses the EXISTING single-position DELETE endpoint (no new
  // backend API) — called once per selected position. Only exposed to
  // fullAccess users, matching the existing per-row Delete button's
  // permission gate. This is the only bulk action exposed, because it's the
  // only one with real, already-existing backend support; per the
  // instructions, no other bulk action (e.g. "reassign recruiter") is
  // invented here without a corresponding existing bulk API.
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const onBulkDelete = async () => {
    if (selectedCodes.size === 0) return;
    if (
      !window.confirm(
        `Delete ${selectedCodes.size} selected position(s)? This cannot be undone.`,
      )
    )
      return;
    setBulkDeleting(true);
    const codes = [...selectedCodes];
    let failCount = 0;
    for (const code of codes) {
      try {
        await API.delete(`/jobs/${code}`);
      } catch (err) {
        failCount++;
        console.error("[JobRequirements] bulk delete failed for", code, err);
      }
    }
    setBulkDeleting(false);
    clearSelection();
    fetchAll();
    if (failCount === 0) {
      toast.success(`Deleted ${codes.length} position(s)`);
    } else {
      toast.error(
        `Deleted ${codes.length - failCount} of ${codes.length}; ${failCount} failed`,
      );
    }
  };

  // ── Bulk status change / recruiter assign ───────────────────────────────
  // Both reuse the exact same full-object-safe update already used by
  // onSave/the edit form: each selected job's COMPLETE current record
  // (from `jobs`, the source of truth) is spread with only the one field
  // overridden, then PUT back via the existing single-position endpoint —
  // never a partial payload, never a second/duplicate update
  // implementation.
  const [bulkStatusOpen, setBulkStatusOpen] = useState(false);
  const [bulkStatusValue, setBulkStatusValue] = useState("ACTIVE");
  const [bulkAssignOpen, setBulkAssignOpen] = useState(false);
  const [bulkAssignValue, setBulkAssignValue] = useState("");
  const [bulkActing, setBulkActing] = useState(false);

  const runBulkFieldUpdate = async (field, value, successVerb) => {
    setBulkActing(true);
    const codes = [...selectedCodes];
    let failCount = 0;
    for (const code of codes) {
      const full = jobs.find((j) => j.jobCode === code);
      if (!full) {
        failCount++;
        continue;
      }
      try {
        await API.put(`/jobs/${encodeURIComponent(code)}`, {
          ...full,
          [field]: value,
        });
      } catch (err) {
        failCount++;
        console.error(
          `[JobRequirements] bulk ${field} update failed for`,
          code,
          err,
        );
      }
    }
    setBulkActing(false);
    clearSelection();
    fetchAll();
    if (failCount === 0) {
      toast.success(`${successVerb} for ${codes.length} position(s)`);
    } else {
      toast.error(
        `${successVerb}: ${codes.length - failCount} of ${codes.length} succeeded; ${failCount} failed`,
      );
    }
  };

  const onBulkExport = () => {
    const rows = jobs.filter((j) => selectedCodes.has(j.jobCode));
    exportToExcel(rows);
    clearSelection();
  };

  // ── Export ──────────────────────────────────────────────────────────────────

  const exportToExcel = (sourceRows = visibleAfterView) => {
    const rows = sourceRows.map((j) => ({
      "Job Code": j.jobCode,
      "Position Date": j.positionDate || "",
      "Company Name": j.companyName,
      "Assigned Recruiter": j.assignedRecruiter || "",
      Domain: j.domain,
      "Role Name": j.roleName,
      Skills: j.skills,
      "Relevant Skills": j.relevantSkills,
      "Mandatory Skills": j.mandatorySkills,
      "Job Description": j.jobDescription,
      Salary: j.salary,
      Experience: j.experience,
      "Relevant Exp": j.relevantExperience,
      Location: j.location,
      "SPOC Name": j.spocName,
      "Number of Positions": j.numberOfPositions ?? "",
      Status: POSITION_STATUS_LABEL[j.status] || "Open",
      "Last Updated By": j.updatedBy || "",
      "Last Updated At": j.updatedAt || "",
      Candidates: (candsByJob[j.jobCode] || []).length,
      Recruiters: recruiterCount(j.jobCode),
      "Created By": j.createdBy || "",
      "Created At": j.createdAt,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Positions");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      sourceRows === visibleAfterView
        ? `Positions_${todayStr}.xlsx`
        : `Positions_selected_${todayStr}.xlsx`,
    );
    toast.success(
      `Excel exported (${rows.length} position${rows.length === 1 ? "" : "s"})`,
    );
  };

  // ── Save (create or update) ─────────────────────────────────────────────────

  const onSave = async () => {
    if (!form.jobCode?.trim()) {
      toast.error("Job code is required");
      return;
    }
    if (/[/\\]/.test(form.jobCode)) {
      toast.error(
        "Job Code can't contain '/' or '\\' — these break edit/delete links. Use '-' instead.",
      );
      return;
    }
    if (!form.companyName?.trim()) {
      toast.error("Company name is required");
      return;
    }
    if (!form.roleName?.trim()) {
      toast.error("Role name is required");
      return;
    }
    if (!form.assignedRecruiter?.trim()) {
      toast.error("Assigned Recruiter is required");
      return;
    }

    // Hard guard against double-submit (ref is synchronous, no render delay)
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    // Normalize the (possibly newly-typed) company name so trailing spaces
    // don't create a near-duplicate entry in the dropdown.
    const payload = {
      ...form,
      jobCode: (form.jobCode || "").trim(),
      companyName: (form.companyName || "").trim(),
    };
    try {
      if (editId) {
        await API.put(`/jobs/${encodeURIComponent(editId.trim())}`, payload);
        toast.success("Position updated");
      } else {
        await API.post("/jobs", payload);
        toast.success("Position saved");
      }
      resetForm();
      fetchAll();
    } catch (err) {
      // The backend now always returns a correctly-classified status
      // (401 only for genuine auth failure, 403 only for genuine
      // permission denial, 409 for the duplicate-job-code business rule,
      // 404 when the position is genuinely gone, 400 for validation) with
      // a real, specific message — so no more guessing/overriding here.
      toast.error(getErrorMessage(err, "Could not save position"));
      console.error("[JobRequirements] save error:", {
        status: err.response?.status,
        errorCode: err.response?.data?.errorCode,
        msg: extractErrorMessage(err),
        editId,
        jobCode: form.jobCode,
      });
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  // ── Edit ─────────────────────────────────────────────────────────────────────

  const onEdit = (j) => {
    setForm({
      ...emptyForm,
      ...j,
      jobDescription: j.jobDescription || "",
      positionDate: j.positionDate ? String(j.positionDate).split("T")[0] : "",
    });
    setEditId(j.jobCode);
    setShowForm(true);
    loadSpocsForCompany(j.companyName); // populate SPOC dropdown for this company
    window.scrollTo(0, 0);
  };

  // ── Delete ───────────────────────────────────────────────────────────────────

  const onDelete = async (jobCode) => {
    if (!window.confirm("Delete this position?")) return;
    try {
      await API.delete(`/jobs/${jobCode}`);
      toast.success("Position deleted");
      fetchAll();
    } catch (err) {
      toast.error(getErrorMessage(err, "Could not delete position"));
      console.error("[JobRequirements] delete error:", {
        status: err.response?.status,
        errorCode: err.response?.data?.errorCode,
        msg: extractErrorMessage(err),
        jobCode,
      });
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <PageContainer
      sx={{
        px: { xs: 2, md: 2.5 },
        py: { xs: 2, md: 2 },
        overflowX: "hidden",
      }}
    >
      {/* ── Header ── */}
      <Box sx={{ mb: 0.5, pl: { xs: 5, md: 0 } }}>
        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
          {jobs.length} role{jobs.length !== 1 ? "s" : ""} ·{" "}
          {
            jobs.filter(
              (j) =>
                (j.status || "ACTIVE") !== "ON_HOLD" &&
                (j.status || "ACTIVE") !== "CLOSED",
            ).length
          }{" "}
          open ·{" "}
          {
            jobs.filter((j) => (candsByJob[j.jobCode] || []).length === 0)
              .length
          }{" "}
          with no profiles submitted
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          pl: { xs: 5, md: 0 },
        }}
      >
        <Typography
          sx={{ color: "text.secondary", fontSize: 14, fontWeight: 600 }}
        >
          {visible.length} of {jobs.length} positions
        </Typography>

        <Stack
          direction="row"
          spacing={1.5}
          flexWrap="wrap"
          useFlexGap
          alignItems="center"
        >
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              display: { xs: "none", md: "flex" },
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1.5,
              px: 2,
              py: 1,
            }}
          >
            {[
              { c: "success.main", l: "Fresh ≤1 day" },
              { c: "warning.main", l: "Aging 2-3 days" },
              { c: "error.main", l: "Old >3 days" },
            ].map((d) => (
              <Stack
                key={d.l}
                direction="row"
                spacing={0.75}
                alignItems="center"
              >
                <Box
                  sx={{
                    width: 9,
                    height: 9,
                    borderRadius: "50%",
                    bgcolor: d.c,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 12,
                    color: "text.secondary",
                    fontWeight: 600,
                  }}
                >
                  {d.l}
                </Typography>
              </Stack>
            ))}
          </Stack>

          <RefreshButton onClick={fetchAll} />

          {fullAccess && (
            <Button
              variant={showForm ? "outlined" : "contained"}
              startIcon={showForm ? <CloseIcon /> : <AddIcon />}
              onClick={() => {
                setShowForm(!showForm);
                setForm(emptyForm);
                setEditId(null);
              }}
            >
              {showForm ? "Close" : "Add Position"}
            </Button>
          )}

          {jobs.length > 0 && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportToExcel}
            >
              Export Excel
            </Button>
          )}
        </Stack>
      </Box>

      {/* ── Add / Edit Form ── */}
      <Collapse in={showForm} unmountOnExit>
        <Paper sx={{ p: { xs: 2.5, md: 3.5 }, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2.5 }}>
            {editId ? "Edit Position" : "New Position"}
          </Typography>

          <Grid container spacing={2}>
            {fields.map((f) => (
              <Grid item xs={12} sm={6} md={4} key={f.key}>
                {f.key === "companyName" ? (
                  // STRICT dropdown of registered client companies (no typing).
                  // Register new companies on the Clients page; picking one here
                  // loads that company's SPOCs into the SPOC field below.
                  <Autocomplete
                    options={clientCompanies.map((c) => c.name)}
                    value={form.companyName || ""}
                    onChange={(e, newValue) => {
                      const name = newValue || "";
                      setForm({ ...form, companyName: name, spocName: "" });
                      loadSpocsForCompany(name);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={
                          <span>
                            Company Name
                            <span style={{ color: "#dc2626" }}> *</span>
                          </span>
                        }
                        placeholder="Select a registered company"
                      />
                    )}
                  />
                ) : f.key === "spocName" ? (
                  // Free-solo: pick a registered SPOC of the selected company,
                  // OR type a new name.
                  <Autocomplete
                    freeSolo
                    options={companySpocs.map((s) => s.name)}
                    value={form.spocName || ""}
                    onChange={(e, newValue) =>
                      setForm({ ...form, spocName: (newValue || "").trim() })
                    }
                    onInputChange={(e, newInputValue, reason) => {
                      if (reason === "input") {
                        setForm({ ...form, spocName: newInputValue });
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="SPOC Name"
                        placeholder={
                          form.companyName
                            ? "Select or type a SPOC"
                            : "Select a company first"
                        }
                      />
                    )}
                  />
                ) : (
                  <TextField
                    label={
                      f.required ? (
                        <span>
                          {f.label}
                          <span style={{ color: "#dc2626" }}> *</span>
                        </span>
                      ) : (
                        f.label
                      )
                    }
                    name={f.key}
                    type={f.type || "text"}
                    value={form[f.key] || ""}
                    onChange={onChange}
                    disabled={f.key === "jobCode" && !!editId}
                    InputLabelProps={
                      f.type === "date" ? { shrink: true } : undefined
                    }
                    placeholder={
                      f.type === "date"
                        ? undefined
                        : `Enter ${f.label.toLowerCase()}`
                    }
                  />
                )}
              </Grid>
            ))}

            <Grid item xs={12} sm={6} md={4}>
              {/* Assigned Recruiter: MULTI-select of RECRUITER/MANAGER users;
                  also typeable. Stored as a comma-separated string in
                  form.assignedRecruiter, shown here as chips. */}
              <Autocomplete
                multiple
                freeSolo
                options={recruiterOptions}
                value={
                  form.assignedRecruiter
                    ? form.assignedRecruiter
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean)
                    : []
                }
                onChange={(e, newValue) =>
                  setForm({
                    ...form,
                    // de-dupe + join back to a comma-separated string
                    assignedRecruiter: [
                      ...new Set(newValue.map((s) => s.trim()).filter(Boolean)),
                    ].join(", "),
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={
                      <span>
                        Assigned Recruiter
                        <span style={{ color: "#dc2626" }}> *</span>
                      </span>
                    }
                    placeholder="Select or type recruiters"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select
                label="Status"
                name="status"
                value={form.status || "ACTIVE"}
                onChange={onChange}
              >
                {POSITION_STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {posStatusLabel(s)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Job Description"
                name="jobDescription"
                value={form.jobDescription || ""}
                onChange={onChange}
                placeholder="Describe the role, responsibilities, and requirements…"
                multiline
                minRows={3}
              />
            </Grid>
          </Grid>

          <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
            <Button
              variant="contained"
              onClick={saving ? undefined : onSave}
              disabled={saving}
            >
              {saving
                ? "Saving…"
                : editId
                  ? "Update Position"
                  : "Save Position"}
            </Button>
            <Button variant="text" color="inherit" onClick={resetForm}>
              Cancel
            </Button>
          </Stack>
        </Paper>
      </Collapse>

      {/* ── Search + date + company/recruiter filters (one compact toolbar) ── */}
      <FilterBar>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search job code, company, role, domain, location…"
          sx={{ flex: 1, minWidth: 220 }}
        />

        <DateFilter
          from={fromDate}
          to={toDate}
          onFromChange={setFromDate}
          onToChange={setToDate}
          isToday={fromDate === todayStr && toDate === todayStr}
          isAllTime={!fromDate && !toDate}
          onToday={() => {
            setFromDate(todayStr);
            setToDate(todayStr);
          }}
          onAllTime={() => {
            setFromDate("");
            setToDate("");
          }}
        />
        <TextField
          select
          size="small"
          label="Company"
          value={filterCompany}
          onChange={(e) => setFilterCompany(e.target.value)}
          sx={{ minWidth: 150, maxWidth: 190 }}
        >
          <MenuItem value="">All Companies</MenuItem>
          {[
            ...new Set([
              ...clientCompanies.map((c) => c.name),
              ...companyOptions,
            ]),
          ]
            .sort((a, b) => a.localeCompare(b))
            .map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Assigned Recruiter"
          value={filterRecruiter}
          onChange={(e) => setFilterRecruiter(e.target.value)}
          sx={{ minWidth: 150, maxWidth: 190 }}
        >
          <MenuItem value="">All Recruiters</MenuItem>
          {recruiterOptions.map((r) => (
            <MenuItem key={r} value={r}>
              {r}
            </MenuItem>
          ))}
        </TextField>
        <FilterChipBar
          options={[
            { key: "Fresh", label: "Fresh ≤1d" },
            { key: "Aging", label: "Aging 2-3d" },
            { key: "Old", label: "Old >3d" },
          ]}
          selected={ageFilter ? [ageFilter] : []}
          onToggle={(key) => setAgeFilter(ageFilter === key ? "" : key)}
        />
        <Stack direction="row" spacing={0} sx={{ ml: "auto" }}>
          <Button
            size="small"
            variant={density === "comfortable" ? "contained" : "outlined"}
            onClick={() => setDensity("comfortable")}
            sx={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
          >
            Comfortable
          </Button>
          <Button
            size="small"
            variant={density === "compact" ? "contained" : "outlined"}
            onClick={() => setDensity("compact")}
            sx={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
          >
            Compact
          </Button>
        </Stack>
      </FilterBar>

      {/* ── Phase 7 (item G.1): Saved view tabs, real counts from `visible` above ── */}
      <Paper sx={{ mb: 2 }}>
        <SavedViewTabs
          views={savedViewDefs}
          activeKey={savedView}
          onChange={setSavedView}
        />
      </Paper>

      {/* ── Table ── */}
      {visibleAfterView.length === 0 ? (
        <Paper sx={{ py: 2 }}>
          <EmptyState
            icon={<WorkOutlineIcon sx={{ fontSize: 28, color: ACCENT }} />}
            title="No positions found"
            description={
              search || fromDate || toDate || savedView !== "all"
                ? "Try clearing your search, date filter, or saved view."
                : "Add your first position to get started."
            }
          />
        </Paper>
      ) : (
        <>
        <Paper sx={{ overflow: "hidden" }}>
          {/* ── Bulk action bar: Delete, Change status, Assign recruiter, Export ── */}
          {fullAccess && (
            <BulkActionBar
              count={selectedCodes.size}
              onClear={clearSelection}
              actions={[
                { label: "Export selected", onClick: onBulkExport },
                {
                  label: "Change status…",
                  onClick: () => setBulkStatusOpen(true),
                  disabled: bulkActing,
                },
                {
                  label: "Assign recruiter…",
                  onClick: () => setBulkAssignOpen(true),
                  disabled: bulkActing,
                },
                {
                  label: bulkDeleting ? "Deleting…" : "Delete selected",
                  onClick: onBulkDelete,
                  disabled: bulkDeleting,
                },
              ]}
            />
          )}
          <TableContainer
            sx={{
              // Option A: cap the table height so the horizontal scrollbar is
              // ALWAYS in view. Rows scroll vertically inside this box, the
              // header stays stuck at top, and the horizontal bar sits at the
              // bottom of the box — reachable without scrolling the whole page.
              maxHeight: "calc(100vh - 175px)",
              overflowX: "scroll",
              overflowY: "auto",
              // WebKit (Chrome/Edge): force the horizontal bar to always render.
              "&::-webkit-scrollbar": {
                height: 12,
                width: 12,
                WebkitAppearance: "none",
                display: "block",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#e2e8f0",
                borderRadius: 8,
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#94a3b8",
                borderRadius: 8,
                border: "2px solid #e2e8f0",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#64748b",
              },
              // Firefox
              scrollbarWidth: "auto",
              scrollbarColor: "#94a3b8 #e2e8f0",
            }}
          >
            <Table
              stickyHeader
              size={density === "compact" ? "small" : "medium"}
              sx={{
                minWidth: 900,
                // Clean, flat rows — horizontal separators only (no boxy grid).
                "& .MuiTableCell-root": {
                  borderBottom: "1px solid #eef1f5",
                  borderRight: "none",
                  borderLeft: "none",
                  borderTop: "none",
                  py: 0.6,
                  px: 1.5,
                  fontSize: 13,
                  lineHeight: 1.35,
                  verticalAlign: "middle",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 220,
                },
                "& tbody tr:hover": {
                  backgroundColor: "#f6f8fb",
                },
                // Flat, uppercase header — no fill box, subtle bottom border.
                "& thead .MuiTableCell-root": {
                  backgroundColor: "#ffffff",
                  color: "#64748b",
                  fontWeight: 700,
                  fontSize: 11.5,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  borderBottom: "2px solid #e6eaf0",
                  whiteSpace: "nowrap",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  {fullAccess && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        indeterminate={
                          selectedCodes.size > 0 &&
                          !visibleAfterView.every((j) =>
                            selectedCodes.has(j.jobCode),
                          )
                        }
                        checked={
                          visibleAfterView.length > 0 &&
                          visibleAfterView.every((j) =>
                            selectedCodes.has(j.jobCode),
                          )
                        }
                        onChange={toggleSelectAllVisible}
                      />
                    </TableCell>
                  )}
                  <TableCell sx={{ width: 40 }} />
                  <TableCell>Job Code</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Assigned To</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Experience</TableCell>
                  <TableCell>Relevant Exp</TableCell>
                  <TableCell>Job Description</TableCell>
                  <TableCell>Salary</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>SPOC</TableCell>
                  <TableCell align="center">Vacancies</TableCell>
                  {fullAccess && <TableCell>Recruiters</TableCell>}
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell>Freshness</TableCell>
                  <TableCell align="center">Candidates</TableCell>
                  <TableCell align="center">View</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {visiblePage.map((j) => {
                  const fr = freshness(j);
                  const list = candsByJob[j.jobCode] || [];
                  const rc = recruiterCount(j.jobCode);
                  const rNames = recruiterNames(j.jobCode);
                  const isOpen = openRow === j.jobCode;

                  return (
                    <Fragment key={j.jobCode}>
                      <TableRow
                        hover
                        sx={{
                          "& > *": { borderBottom: "unset" },
                          cursor: "pointer",
                        }}
                        onClick={() => setOpenRow(isOpen ? null : j.jobCode)}
                      >
                        {fullAccess && (
                          <TableCell
                            padding="checkbox"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Checkbox
                              size="small"
                              checked={selectedCodes.has(j.jobCode)}
                              onChange={() => toggleSelect(j.jobCode)}
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <KeyboardArrowRightIcon
                            fontSize="small"
                            sx={{
                              transition: "transform .2s",
                              transform: isOpen ? "rotate(90deg)" : "none",
                              color: "text.secondary",
                            }}
                          />
                        </TableCell>

                        <TableCell
                          sx={{ fontFamily: "monospace", fontWeight: 700 }}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Box
                              sx={{
                                width: 9,
                                height: 9,
                                borderRadius: "50%",
                                bgcolor: `${fr.color}.main`,
                              }}
                            />
                            {j.jobCode}
                          </Stack>
                        </TableCell>

                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {fmtDate(j.positionDate || j.createdAt)}
                        </TableCell>

                        <TableCell sx={{ fontWeight: 600 }}>
                          {j.companyName}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {j.assignedRecruiter || "—"}
                        </TableCell>
                        <TableCell>{j.roleName || "—"}</TableCell>

                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {j.experience || "—"}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {j.relevantExperience || "—"}
                        </TableCell>

                        <TableCell
                          sx={{ width: 240, minWidth: 240, maxWidth: 240 }}
                        >
                          {j.jobDescription ? (
                            <Tooltip title="Click the row to read the full description">
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  lineHeight: 1.35,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {j.jobDescription}
                              </Typography>
                            </Tooltip>
                          ) : (
                            "—"
                          )}
                        </TableCell>

                        <TableCell sx={{ fontWeight: 700 }}>
                          {j.salary || "—"}
                        </TableCell>

                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                          >
                            <PlaceIcon
                              sx={{ fontSize: 14, color: "text.secondary" }}
                            />
                            {j.location || "—"}
                          </Stack>
                        </TableCell>

                        <TableCell>{j.spocName || "—"}</TableCell>

                        <TableCell align="center">
                          {j.numberOfPositions != null &&
                          j.numberOfPositions !== ""
                            ? j.numberOfPositions
                            : "—"}
                        </TableCell>

                        {fullAccess && (
                          <TableCell sx={{ maxWidth: 200 }}>
                            {rNames.length === 0 ? (
                              <Typography
                                sx={{ fontSize: 13, color: "text.secondary" }}
                              >
                                —
                              </Typography>
                            ) : (
                              <Stack
                                direction="row"
                                spacing={0.5}
                                flexWrap="wrap"
                                useFlexGap
                              >
                                {rNames.slice(0, 2).map((n) => (
                                  <Chip
                                    key={n}
                                    size="small"
                                    label={n}
                                    variant="outlined"
                                  />
                                ))}
                                {rNames.length > 2 && (
                                  <Tooltip title={rNames.slice(2).join(", ")}>
                                    <Chip
                                      size="small"
                                      label={`+${rNames.length - 2}`}
                                      variant="outlined"
                                    />
                                  </Tooltip>
                                )}
                              </Stack>
                            )}
                          </TableCell>
                        )}

                        <TableCell>
                          <StatusBadge
                            label={posStatusLabel(j.status)}
                            tone={
                              MUI_COLOR_TO_TONE[posStatusColor(j.status)] ||
                              "neutral"
                            }
                          />
                        </TableCell>

                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {j.createdAt ? (
                            <>
                              <Box sx={{ fontSize: 12, fontWeight: 600 }}>
                                {j.createdBy || "—"}
                              </Box>
                              <Box
                                sx={{ fontSize: 11, color: "text.secondary" }}
                              >
                                {new Date(j.updatedAt + "Z").toLocaleString(
                                  "en-IN",
                                  {
                                    timeZone: "Asia/Kolkata",
                                  },
                                )}
                              </Box>
                            </>
                          ) : (
                            "—"
                          )}
                        </TableCell>

                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {j.updatedAt ? (
                            <>
                              <Box sx={{ fontSize: 12, fontWeight: 600 }}>
                                {j.updatedBy || "—"}
                              </Box>
                              <Box
                                sx={{ fontSize: 11, color: "text.secondary" }}
                              >
                                {new Date(j.updatedAt + "Z").toLocaleString(
                                  "en-IN",
                                  {
                                    timeZone: "Asia/Kolkata",
                                  },
                                )}
                              </Box>
                            </>
                          ) : (
                            "—"
                          )}
                        </TableCell>

                        <TableCell>
                          <StatusBadge
                            label={fr.label}
                            tone={MUI_COLOR_TO_TONE[fr.color] || "neutral"}
                          />
                        </TableCell>

                        <TableCell align="center">
                          {list.length === 0 ? (
                            <Chip
                              size="small"
                              label={0}
                              variant="outlined"
                              sx={{ fontWeight: 700, minWidth: 36 }}
                            />
                          ) : (
                            (() => {
                              const stageCounts = {};
                              list.forEach((c) => {
                                stageCounts[c.feedback] =
                                  (stageCounts[c.feedback] || 0) + 1;
                              });
                              const segments = Object.entries(stageCounts).map(
                                ([stage, n]) => ({
                                  stage,
                                  n,
                                  pct: (n / list.length) * 100,
                                  color: MINI_BAR_COLOR[stage] || "#94a3b8",
                                }),
                              );
                              const breakdown = segments
                                .map((s) => `${statusLabel(s.stage)}: ${s.n}`)
                                .join(" · ");
                              return (
                                <Tooltip title={breakdown}>
                                  <Stack
                                    direction="row"
                                    spacing={0.75}
                                    alignItems="center"
                                    justifyContent="center"
                                  >
                                    <Typography
                                      sx={{
                                        fontWeight: 700,
                                        fontSize: 13,
                                        minWidth: 18,
                                      }}
                                    >
                                      {list.length}
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        width: 56,
                                        height: 8,
                                        borderRadius: "4px",
                                        overflow: "hidden",
                                        bgcolor: "action.hover",
                                      }}
                                    >
                                      {segments.map((s) => (
                                        <Box
                                          key={s.stage}
                                          sx={{
                                            width: `${s.pct}%`,
                                            bgcolor: s.color,
                                          }}
                                        />
                                      ))}
                                    </Box>
                                  </Stack>
                                </Tooltip>
                              );
                            })()
                          )}
                        </TableCell>

                        <TableCell
                          align="center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <IconButton
                            size="small"
                            onClick={() => {
                              setDrawerJob(j);
                              setDrawerTab(0);
                            }}
                            aria-label={`View ${j.jobCode}`}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </TableCell>

                        <TableCell
                          align="right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="flex-end"
                          >
                            {fullAccess && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EditIcon sx={{ fontSize: 15 }} />}
                                onClick={() => onEdit(j)}
                              >
                                Edit
                              </Button>
                            )}
                            {fullAccess && (
                              <Button
                                size="small"
                                color="error"
                                variant="outlined"
                                startIcon={<DeleteIcon sx={{ fontSize: 15 }} />}
                                onClick={() => onDelete(j.jobCode)}
                              >
                                Del
                              </Button>
                            )}
                            {!fullAccess && (
                              <Typography
                                sx={{ fontSize: 12, color: "text.disabled" }}
                              >
                                View only
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>

                      <TableRow key={j.jobCode + "-detail"}>
                        <TableCell
                          sx={{
                            py: 0,
                            borderBottom: isOpen ? undefined : "none",
                            whiteSpace: "normal",
                            maxWidth: "none",
                            overflow: "visible",
                          }}
                          colSpan={fullAccess ? 20 : 19}
                        >
                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <Box
                              sx={{
                                m: 1,
                                ml: 5,
                                p: 2,
                                bgcolor: "background.default",
                                borderRadius: 1.5,
                              }}
                            >
                              <Box sx={{ mb: 2 }}>
                                <Typography
                                  sx={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    color: "text.secondary",
                                    textTransform: "uppercase",
                                    letterSpacing: 0.6,
                                    mb: 1,
                                  }}
                                >
                                  Position Details
                                </Typography>
                                <Grid container spacing={1.5}>
                                  {[
                                    { label: "Domain", value: j.domain },
                                    { label: "Skills", value: j.skills },
                                    {
                                      label: "Relevant Skills",
                                      value: j.relevantSkills,
                                    },
                                    {
                                      label: "Mandatory Skills",
                                      value: j.mandatorySkills,
                                    },
                                  ].map((d) => (
                                    <Grid item xs={6} sm={4} key={d.label}>
                                      <Typography
                                        sx={{
                                          fontSize: 10,
                                          color: "text.secondary",
                                          fontWeight: 700,
                                          textTransform: "uppercase",
                                          letterSpacing: 0.5,
                                        }}
                                      >
                                        {d.label}
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontSize: 13,
                                          fontWeight: 600,
                                          whiteSpace: "pre-wrap",
                                          wordBreak: "break-word",
                                        }}
                                      >
                                        {d.value || "—"}
                                      </Typography>
                                    </Grid>
                                  ))}
                                </Grid>
                              </Box>

                              {j.jobDescription && (
                                <Box sx={{ mb: 2 }}>
                                  <Typography
                                    sx={{
                                      fontSize: 10,
                                      fontWeight: 700,
                                      color: "text.secondary",
                                      textTransform: "uppercase",
                                      letterSpacing: 0.6,
                                      mb: 0.5,
                                    }}
                                  >
                                    Job Description
                                  </Typography>
                                  <Typography
                                    sx={{
                                      fontSize: 13,
                                      whiteSpace: "pre-wrap",
                                      lineHeight: 1.6,
                                    }}
                                  >
                                    {j.jobDescription}
                                  </Typography>
                                </Box>
                              )}

                              <Typography
                                sx={{ fontWeight: 700, fontSize: 13, mb: 1.5 }}
                              >
                                {fullAccess ? (
                                  <>
                                    Candidates submitted for {j.roleName} @{" "}
                                    {j.companyName} · {list.length} candidate
                                    {list.length !== 1 ? "s" : ""} from {rc}{" "}
                                    recruiter{rc !== 1 ? "s" : ""}
                                  </>
                                ) : (
                                  <>
                                    Your candidates for {j.roleName} @{" "}
                                    {j.companyName} · {list.length}
                                  </>
                                )}
                              </Typography>

                              {list.length === 0 ? (
                                <Typography
                                  sx={{ color: "text.secondary", fontSize: 13 }}
                                >
                                  {fullAccess
                                    ? "No candidates submitted for this position yet."
                                    : "You haven't submitted any candidates for this position yet."}
                                </Typography>
                              ) : (
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Candidate</TableCell>
                                      {fullAccess && (
                                        <TableCell>Recruiter</TableCell>
                                      )}
                                      <TableCell>Current Company</TableCell>
                                      <TableCell>Experience</TableCell>
                                      <TableCell>Status</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {list.map((c) => (
                                      <TableRow key={c.id}>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                          {candidateName(c)}
                                        </TableCell>
                                        {fullAccess && (
                                          <TableCell>
                                            {c.recruiterName || "—"}
                                          </TableCell>
                                        )}
                                        <TableCell>
                                          {c.companyName || "—"}
                                        </TableCell>
                                        <TableCell>
                                          {c.totalExperience ||
                                            c.experienceYears ||
                                            "—"}
                                        </TableCell>
                                        <TableCell>
                                          <StatusBadge
                                            label={statusLabel(c.feedback)}
                                            tone={
                                              MUI_COLOR_TO_TONE[
                                                STATUS_COLOR[c.feedback] ||
                                                  "default"
                                              ] || "neutral"
                                            }
                                          />
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
          <Pagination
            count={pageCount}
            page={Math.min(tablePage, pageCount)}
            onChange={(_, value) => setTablePage(value)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Stack>
        </>
      )}

      {/* ── Phase 7 (item G.8): SideDrawer for position detail ──────────────────
          Opened via the new "View" (eye) icon per row, added above. Tabs:
          - Details: the same fields already shown in the inline expand row
            above (job description, salary, location, SPOC, vacancies) —
            reusing real data already loaded, nothing new fetched.
          - Pipeline: the same candidate list already shown in the inline
            expand's candidate sub-table (candsByJob) — real data.
          - Activity / Notes: the prototype shows these tabs, but this
            application has no activity-log or notes API/data model for
            positions. Rather than fabricate content, these tabs show an
            honest empty state explaining that, per the instruction not to
            create fake tabs with fake data.
      */}
      {drawerJob &&
        (() => {
          const list = candsByJob[drawerJob.jobCode] || [];
          const tabLabels = ["Details", "Pipeline", "Activity", "Notes"];
          return (
            <SideDrawer
              open={!!drawerJob}
              onClose={() => setDrawerJob(null)}
              title={drawerJob.roleName || drawerJob.jobCode}
              subtitle={`${drawerJob.companyName || "—"} · ${drawerJob.jobCode}`}
              meta={
                <StatusBadge
                  label={posStatusLabel(drawerJob.status)}
                  tone={
                    MUI_COLOR_TO_TONE[posStatusColor(drawerJob.status)] ||
                    "neutral"
                  }
                />
              }
              tabs={tabLabels}
              activeTab={drawerTab}
              onTabChange={setDrawerTab}
              footer={
                fullAccess && (
                  <>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<EditIcon sx={{ fontSize: 15 }} />}
                      onClick={() => {
                        setDrawerJob(null);
                        onEdit(drawerJob);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => {
                        setDrawerJob(null);
                        onDelete(drawerJob.jobCode);
                      }}
                    >
                      Delete
                    </Button>
                  </>
                )
              }
            >
              {tabLabels[drawerTab] === "Details" && (
                <Stack spacing={1.5}>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "text.secondary",
                        fontWeight: 600,
                      }}
                    >
                      Job description
                    </Typography>
                    <Typography sx={{ fontSize: 13, whiteSpace: "pre-wrap" }}>
                      {drawerJob.jobDescription || "—"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "text.secondary",
                        fontWeight: 600,
                      }}
                    >
                      Salary
                    </Typography>
                    <Typography sx={{ fontSize: 13 }}>
                      {drawerJob.salary || "—"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "text.secondary",
                        fontWeight: 600,
                      }}
                    >
                      Location
                    </Typography>
                    <Typography sx={{ fontSize: 13 }}>
                      {drawerJob.location || "—"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "text.secondary",
                        fontWeight: 600,
                      }}
                    >
                      SPOC
                    </Typography>
                    <Typography sx={{ fontSize: 13 }}>
                      {drawerJob.spocName || "—"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "text.secondary",
                        fontWeight: 600,
                      }}
                    >
                      Number of positions
                    </Typography>
                    <Typography sx={{ fontSize: 13 }}>
                      {drawerJob.numberOfPositions != null &&
                      drawerJob.numberOfPositions !== ""
                        ? drawerJob.numberOfPositions
                        : "—"}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 12,
                        color: "text.secondary",
                        fontWeight: 600,
                      }}
                    >
                      Assigned recruiter(s)
                    </Typography>
                    <Typography sx={{ fontSize: 13 }}>
                      {drawerJob.assignedRecruiter || "—"}
                    </Typography>
                  </Box>
                </Stack>
              )}

              {tabLabels[drawerTab] === "Pipeline" &&
                (list.length === 0 ? (
                  <EmptyState
                    title="No candidates yet"
                    description="Candidates submitted against this position will appear here."
                  />
                ) : (
                  <Stack spacing={1}>
                    {list.map((c) => (
                      <Box
                        key={c.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          p: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: "8px",
                        }}
                      >
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                          {candidateName(c)}
                        </Typography>
                        <StatusBadge
                          label={statusLabel(c.feedback)}
                          tone={
                            MUI_COLOR_TO_TONE[
                              STATUS_COLOR[c.feedback] || "default"
                            ] || "neutral"
                          }
                        />
                      </Box>
                    ))}
                  </Stack>
                ))}

              {tabLabels[drawerTab] === "Activity" && (
                <EmptyState
                  title="Activity log not available"
                  description="This application does not currently track a position activity history."
                />
              )}

              {tabLabels[drawerTab] === "Notes" && (
                <EmptyState
                  title="Notes not available"
                  description="This application does not currently have a notes field for positions."
                />
              )}
            </SideDrawer>
          );
        })()}

      {/* Bulk: Change status */}
      <Dialog open={bulkStatusOpen} onClose={() => setBulkStatusOpen(false)}>
        <DialogTitle>
          Change status for {selectedCodes.size} position(s)
        </DialogTitle>
        <DialogContent>
          <TextField
            select
            label="New status"
            value={bulkStatusValue}
            onChange={(e) => setBulkStatusValue(e.target.value)}
            fullWidth
            sx={{ mt: 1, minWidth: 260 }}
          >
            {POSITION_STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {posStatusLabel(s)}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBulkStatusOpen(false)}
            disabled={bulkActing}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={bulkActing}
            onClick={async () => {
              await runBulkFieldUpdate(
                "status",
                bulkStatusValue,
                "Status updated",
              );
              setBulkStatusOpen(false);
            }}
          >
            {bulkActing ? "Updating…" : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk: Assign recruiter */}
      <Dialog open={bulkAssignOpen} onClose={() => setBulkAssignOpen(false)}>
        <DialogTitle>
          Assign recruiter to {selectedCodes.size} position(s)
        </DialogTitle>
        <DialogContent>
          <Autocomplete
            options={recruiters}
            value={bulkAssignValue}
            onChange={(_, v) => setBulkAssignValue(v || "")}
            freeSolo
            renderInput={(params) => (
              <TextField
                {...params}
                label="Recruiter"
                sx={{ mt: 1, minWidth: 260 }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBulkAssignOpen(false)}
            disabled={bulkActing}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={bulkActing || !bulkAssignValue}
            onClick={async () => {
              await runBulkFieldUpdate(
                "assignedRecruiter",
                bulkAssignValue,
                "Recruiter assigned",
              );
              setBulkAssignOpen(false);
            }}
          >
            {bulkActing ? "Assigning…" : "Assign"}
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
}
